import React, { useState, useRef, useEffect } from 'react';

interface VendorComboboxProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
  vendors: string[];
  disabled?: boolean;
}

export const VendorCombobox: React.FC<VendorComboboxProps> = ({
  id,
  value,
  onChange,
  className = '',
  placeholder = 'Search or add vendor...',
  vendors,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const filteredVendors = vendors.filter((vendor) =>
    vendor.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isNewVendor =
    Boolean(searchTerm.trim()) &&
    !vendors.some((vendor) => vendor.toLowerCase() === searchTerm.toLowerCase());

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const newValue = e.target.value;
    setSearchTerm(newValue);
    onChange(newValue);
    setIsOpen(true);
  };

  const handleVendorSelect = (vendor: string) => {
    if (disabled) return;
    setSearchTerm(vendor);
    onChange(vendor);
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const handleFocus = () => {
    if (disabled) return;
    setIsOpen(true);
    setSearchTerm(value);
  };

  const handleBlur = (e: React.FocusEvent) => {
    setTimeout(() => {
      if (!dropdownRef.current?.contains(e.relatedTarget as Node)) {
        setIsOpen(false);
      }
    }, 150);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setSearchTerm(value);
  }, [value]);

  return (
    <div className="relative" ref={dropdownRef}>
      <input
        id={id}
        aria-label={id ? undefined : "Brand or vendor"}
        ref={inputRef}
        type="text"
        value={searchTerm}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={`input disabled:opacity-50 ${className}`}
        placeholder={placeholder}
        autoComplete="off"
      />

      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 surface-raised rounded-lg shadow-elevated max-h-60 overflow-y-auto">
          {filteredVendors.length > 0 && (
            <>
              {filteredVendors.map((vendor) => (
                <button
                  key={vendor}
                  type="button"
                  onClick={() => handleVendorSelect(vendor)}
                  className="w-full text-left px-3 py-2 text-white hover:bg-white/[0.06] focus:bg-[#1a1a1a] focus:outline-none"
                >
                  {vendor}
                </button>
              ))}
              {isNewVendor && <div className="border-t border-white/[0.10] my-1"></div>}
            </>
          )}

          {isNewVendor && (
            <button
              type="button"
              onClick={() => handleVendorSelect(searchTerm.trim())}
              className="w-full text-left px-3 py-2 text-green-400 hover:bg-white/[0.06] focus:bg-[#1a1a1a] focus:outline-none flex items-center"
            >
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                  clipRule="evenodd"
                />
              </svg>
              Add "{searchTerm.trim()}" as new vendor
            </button>
          )}

          {filteredVendors.length === 0 && !isNewVendor && searchTerm.trim() && (
            <div className="px-3 py-2 text-gray-400 text-sm">
              No vendors found matching "{searchTerm}"
            </div>
          )}

          {!searchTerm.trim() && (
            <div className="px-3 py-2 text-gray-400 text-sm">
              Start typing to search vendors or add a new one
            </div>
          )}
        </div>
      )}
    </div>
  );
};
